import { IWorkLogTotal } from '@models';
import { OrderService, OrderWorkLog } from '@services';
import { useEffect, useState } from 'react';
import {
  summariseWorkLogs,
  sumWorkLogsBy,
  useWorkLogClock,
} from '../ServiceOrder/useOrderWorkLogs';

export interface IMasterBuildWorkLogsHook {
  loading: boolean;
  dataSource: OrderWorkLog[];
  hasOpen: boolean;
  totalMinutes: number;
  minutesByTechnician: IWorkLogTotal<number>[];
  minutesByJob: IWorkLogTotal<string>[];
}

/**
 * Every work-log entry on every leg of a build, read-only. Held in the hook rather than the
 * order store: the store's map is the *job's* list, and a job drawer opened over the build page
 * would otherwise replace this one. Entries are edited on their own job, so nothing writes here.
 */
export const useMasterBuildWorkLogs = (
  masterBuildId: string | undefined,
  enabled: boolean
): IMasterBuildWorkLogsHook => {
  const [dataSource, setDataSource] = useState<OrderWorkLog[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!masterBuildId || !enabled) return undefined;
    let cancelled = false;
    const pageSize = 200;

    (async () => {
      setLoading(true);
      try {
        const loaded: OrderWorkLog[] = [];
        let totalCount = 0;
        do {
          // eslint-disable-next-line no-await-in-loop
          const page = await OrderService.getWorkLogs({
            masterBuildId,
            skipCount: loaded.length,
            maxResultCount: pageSize,
          });
          totalCount = page.totalCount;
          loaded.push(...page.items);
          if (page.items.length === 0) break;
        } while (loaded.length < totalCount);
        if (!cancelled) setDataSource(loaded);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [masterBuildId, enabled]);

  const hasOpen = dataSource.some((log) => !log.workedTo);
  const now = useWorkLogClock(hasOpen);

  return {
    loading,
    dataSource,
    hasOpen,
    ...summariseWorkLogs(dataSource, now),
    minutesByJob: sumWorkLogsBy(
      dataSource,
      now,
      (log) => log.orderId,
      (log) => log.orderTitle ?? `#${log.orderId.slice(0, 8)}`
    ),
  };
};
